import React, { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import type { ActionItem, User } from '../../types'
import {
  ACTION_ITEM_ROW_GRID,
  ActionItemCreatedDateCell,
  ActionItemDueDateCell,
  ActionItemOwnerCell,
  ActionItemSourceCell,
  ActionItemTitleCell,
  actionItemDueDateStyle,
} from './ActionItemTable'
import FloatingMenu from '../shared/FloatingMenu'

interface ActionItemRowProps {
  item: ActionItem
  teamId: number
  teamMembers: User[]
  onEdit: (item: ActionItem) => void
  highlighted?: boolean
}

export default function ActionItemRow({ item, teamId, teamMembers, onEdit, highlighted = false }: ActionItemRowProps) {
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(item.title)
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false)
  const [editingDate, setEditingDate] = useState(false)
  const [dateValue, setDateValue] = useState(item.due_date)
  const [showContextMenu, setShowContextMenu] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const ownerTriggerRef = useRef<HTMLButtonElement>(null)
  const contextTriggerRef = useRef<HTMLButtonElement>(null)

  const style = actionItemDueDateStyle(item)
  const isCompleted = item.status === 'completed'
  const isCarriedOver = item.status === 'carried_over'

  useEffect(() => {
    if (editingTitle && titleInputRef.current) titleInputRef.current.focus()
  }, [editingTitle])

  useEffect(() => {
    if (editingDate && dateInputRef.current) dateInputRef.current.focus()
  }, [editingDate])

  function handleComplete() {
    router.post(`/teams/${teamId}/action_items/${item.id}/complete`, {}, { preserveScroll: true })
  }

  const titleSaving = useRef(false)
  function handleTitleSave() {
    if (titleSaving.current || !editingTitle) return
    setEditingTitle(false)
    if (titleValue.trim() && titleValue !== item.title) {
      titleSaving.current = true
      router.put(`/teams/${teamId}/action_items/${item.id}`, {
        action_item: { title: titleValue.trim() }
      }, {
        preserveScroll: true,
        onFinish: () => { titleSaving.current = false }
      })
    } else {
      setTitleValue(item.title)
    }
  }

  function handleOwnerChange(ownerId: number) {
    router.put(`/teams/${teamId}/action_items/${item.id}`, {
      action_item: { owner_id: ownerId }
    }, { preserveScroll: true })
    setShowOwnerDropdown(false)
  }

  const dateSaving = useRef(false)
  function handleDateSave() {
    if (dateSaving.current || !editingDate) return
    setEditingDate(false)
    if (dateValue && dateValue !== item.due_date) {
      dateSaving.current = true
      router.put(`/teams/${teamId}/action_items/${item.id}`, {
        action_item: { due_date: dateValue }
      }, {
        preserveScroll: true,
        onFinish: () => { dateSaving.current = false }
      })
    } else {
      setDateValue(item.due_date)
    }
  }

  function handleDelete() {
    if (confirm('Delete this action item?')) {
      router.delete(`/teams/${teamId}/action_items/${item.id}`, { preserveScroll: true })
    }
    setShowContextMenu(false)
  }

  return (
    <div
      id={`action-item-${item.id}`}
      className={`${ACTION_ITEM_ROW_GRID} py-2.5 border-b border-gray-100 transition-colors group ${highlighted ? 'bg-[rgba(237,28,36,0.10)] ring-2 ring-inset ring-pinnacle-red' : 'hover:bg-[#F8F9FA]'}`}
      style={{
        borderLeft: isCarriedOver ? '3px solid #B8860B' : (style.borderLeft || 'none'),
        opacity: isCompleted ? 0.5 : 1
      }}
    >
      <button
        onClick={handleComplete}
        className="w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors"
        style={{
          borderColor: isCompleted ? '#648E8A' : '#d1d5db',
          backgroundColor: isCompleted ? '#648E8A' : 'transparent'
        }}
      >
        {isCompleted && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </button>

      <ActionItemTitleCell
        item={item}
        editing={editingTitle}
        value={titleValue}
        onChange={setTitleValue}
        onSave={handleTitleSave}
        onCancel={() => { setTitleValue(item.title); setEditingTitle(false) }}
        inputRef={titleInputRef}
        onStartEdit={() => !isCompleted && setEditingTitle(true)}
      />

      <ActionItemOwnerCell
        item={item}
        members={teamMembers}
        open={showOwnerDropdown}
        onToggle={() => !isCompleted && setShowOwnerDropdown(!showOwnerDropdown)}
        onClose={() => setShowOwnerDropdown(false)}
        onSelect={handleOwnerChange}
        triggerRef={ownerTriggerRef}
      />

      <ActionItemDueDateCell
        item={item}
        editing={editingDate}
        value={dateValue}
        onChange={setDateValue}
        onSave={handleDateSave}
        onCancel={() => { setDateValue(item.due_date); setEditingDate(false) }}
        inputRef={dateInputRef}
        onStartEdit={() => !isCompleted && setEditingDate(true)}
      />

      <ActionItemCreatedDateCell item={item} />
      <ActionItemSourceCell item={item} />

      <div className="relative">
        <button
          ref={contextTriggerRef}
          onClick={() => setShowContextMenu(!showContextMenu)}
          aria-label="Action item options"
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#6b7280">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
          </svg>
        </button>
        <FloatingMenu
          open={showContextMenu}
          onClose={() => setShowContextMenu(false)}
          anchorRef={contextTriggerRef}
          width={144}
          className="bg-white border border-gray-200 rounded-lg shadow-lg w-36"
        >
            <button
              onClick={() => { onEdit(item); setShowContextMenu(false) }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F8F9FA] flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(237,28,36,0.15)] text-[#ED1C24] flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
        </FloatingMenu>
      </div>
    </div>
  )
}
